import { FC, useCallback, useEffect, useMemo, useRef } from "react";

import { copy } from "shared/assets";

import { PermissionField, PermissionFieldBaseProps } from "./components";
import { useDisabledDependency, useFirstCheckedGroupPermission } from "./helpers";

const {
  permissions: { editPermissions: permissionsCopy },
} = copy;

interface PermissionProps extends PermissionFieldBaseProps {
  dependencies?: string[];
  uncheckDependencies?: string[];
  setValue: (name: string, checked: boolean) => void;
  disabledDependencies?: string[];
  watchAllFields: { [name: string]: boolean };
  rootGroupId: string;
}

export const Permission: FC<PermissionProps> = ({
  name,
  value,
  locked,
  dependencies,
  uncheckDependencies,
  setValue,
  disabledDependencies,
  watchAllFields,
  rootGroupId,
  ...rest
}) => {
  const { isDisabled } = useDisabledDependency({
    watchAllFields,
    disabledDependencies,
  });

  const { firstCheckedGroupPermission } = useFirstCheckedGroupPermission({
    name,
    rootGroupId,
    watchAllFields,
  });

  const updateDependencies = useCallback(
    (checked: boolean) => {
      if (checked) {
        // if this field value changes
        // update the values of all dependencies
        dependencies?.forEach(dependency => {
          !watchAllFields[dependency] && setValue(dependency, true);
        });

        return;
      }

      // uncheck dependencies
      uncheckDependencies?.forEach(dependency => {
        watchAllFields[dependency] && setValue(dependency, false);
      });
    },
    [dependencies, setValue, uncheckDependencies, watchAllFields]
  );

  const lastValue = useRef(value);
  useEffect(() => {
    if (lastValue.current === value) return;

    lastValue.current = value;

    if (value) {
      updateDependencies(true);
      return;
    }

    updateDependencies(false);
  }, [value, updateDependencies]);

  const tooltip = useMemo(() => {
    if (locked) {
      return permissionsCopy.messages.disabledLocked;
    }

    if (rest.label === "View all events") {
      return permissionsCopy.messages.disabledViewAllEvents;
    }

    return permissionsCopy.messages.disabled
      .replace("%s", firstCheckedGroupPermission?.label!)
      .toString();
  }, [firstCheckedGroupPermission?.label, locked, rest.label]);

  return (
    <PermissionField
      name={name}
      value={value}
      disabled={isDisabled}
      isActive={value}
      locked={locked}
      hoverText={tooltip}
      {...rest}
    />
  );
};
